'use client';

import { useTimeSettings } from '@mnd-frontend/contexts';
import type { Sentiment } from '@mnd-frontend/graphql-schema';
import { MonitorMediaFilterType, MonitorSourceVerification } from '@mnd-frontend/graphql-schema';
import IconChevronDown from '@mnd-frontend/icons/components/ChevronDown';
import IconChevronUp from '@mnd-frontend/icons/components/ChevronUp';
import IconExternalLink from '@mnd-frontend/icons/components/ExternalLink';
import IconReach from '@mnd-frontend/icons/components/Reach';
import { toBoolean } from '@mnd-frontend/ts';
import { Avatar, Checkbox } from '@mnd-frontend/ui/publish/components';
import { useTranslation } from '@mnd-frontend/ui/publish/contexts';
import { withLineClamp } from '@mnd-frontend/ui/publish/utils';
import {
  GREY_200,
  GREY_900,
  GREY_1000,
  NEUTRAL_WHITE,
  RADIUS_LG,
  SIZING_1_5,
  SIZING_2,
  SPACING_0_5,
  SPACING_1,
  SPACING_1_5,
  SPACING_2,
  SPACING_3,
  WARNING_100,
} from '@mnd-frontend/ui/shared/tokens';
import { localizeValue } from '@mnd-frontend/utils';
import { Fragment, type MouseEventHandler, useState } from 'react';
import { Link } from 'react-router';
import styled from 'styled-components';
import { formatLinkWithSeekParam } from '../utils/formatLinkWithSeekParam';
import AISummary from './AISummary';
import AudioPlayer from './AudioPlayer';
import Dropdown, { type DropdownItem } from './Dropdown';
import LiveTime from './LiveTime';
import MediaType from './MediaType';
import MentionCardAvatar from './MentionCardAvatar';

const MentionCardContentContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${SPACING_1_5};
`;

const CheckboxContainer = styled.div`
  position: absolute;
  z-index: 1;
  top: ${SPACING_1_5};
  left: ${SPACING_1_5};
  background-color: ${NEUTRAL_WHITE};
  padding: ${SPACING_0_5};
  border-radius: ${props => props.theme.radiusCheckbox};
`;

const MentionCardHeader = styled.div<{ $compact: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: ${props => (props.$compact ? SPACING_1 : SPACING_2)};
`;

const MentionCardHeaderLeft = styled.div<{ $compact: boolean }>`
  display: flex;
  flex-grow: 1;
  align-items: end;
  gap: ${props => (props.$compact ? SPACING_1 : SPACING_1_5)};
`;

const MentionCardHeaderRight = styled.div<{ $compact: boolean }>`
  display: flex;
  align-items: start;
  gap: ${props => (props.$compact ? SPACING_1 : SPACING_1_5)};
`;

const MentionCardHeaderRightContent = styled.div<{ $compact: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => (props.$compact ? SPACING_1 : SPACING_1_5)};
`;

const SourceContainer = styled.div<{ $compact: boolean }>`
  margin-bottom: ${props => (props.$compact ? '2px' : '5px')};
`;

const SourceName = styled.h4<{ $compact: boolean }>`
  font-size: ${props => (props.$compact ? '0.875rem' : SIZING_2)};
  line-height: 1.125;
  color: ${GREY_1000};
  font-weight: 700;
  margin: ${props => (props.$compact ? '0' : '0 0 4px 0')};
  ${withLineClamp(3)}
`;

const Title = styled.h3<{ $compact: boolean }>`
  color: ${GREY_1000};
  font-weight: 700;
  font-size: ${props => (props.$compact ? '1rem' : '1.375rem')};
  line-height: 1.25;
  margin: ${props => (props.$compact ? '0 0 4px 0' : '0 0 8px 0')};
  overflow-wrap: break-word;
  ${withLineClamp(3)}
`;

const Hitline = styled.p<{ $compact: boolean }>`
  color: ${GREY_1000};
  font-weight: 400;
  font-size: ${props => (props.$compact ? SIZING_1_5 : SIZING_2)};
  line-height: 1.375;
  margin: ${props => (props.$compact ? 0 : '0 0 4px 0')};
  overflow-wrap: break-word;

  b {
    font-weight: 600;
    background-color: ${WARNING_100};
  }
`;

const MentionCardFooter = styled.div<{ $compact: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: space-between;
  gap: ${props => (props.$compact ? SPACING_1 : SPACING_3)};
`;

const MetadataLink = styled(Link)`
  color: ${props => props.theme.colorIconsInfo};
  cursor: pointer;
`;

const MetadataExternalLink = styled.a`
  color: ${props => props.theme.colorIconsInfo};
  cursor: pointer;
`;

const Metadata = styled.div<{ $compact: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SPACING_1} ${SPACING_2};
  flex-wrap: wrap;
  font-weight: 500;
  font-size: ${props => (props.$compact ? SIZING_1_5 : '0.875rem')};
  line-height: 1;
  color: ${GREY_900};
`;

const MetadataItem = styled.div`
  display: flex;
  align-items: end;
  gap: ${SPACING_0_5};
`;

const AvatarContainer = styled.div<{ $compact: boolean }>`
  display: flex;
  flex-direction: row-reverse;
  & > *:not(:first-child) {
    margin-right: ${props => (props.$compact ? '-1.5rem' : '-2rem')};
  }
`;

const ShowMultipleAudioHitlines = styled.button`
  all: unset;
  color: ${props => props.theme.colorIconsInfo};
  cursor: pointer;
`;

const AudioPlayerContainer = styled.div<{
  $compact: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: ${props => (props.$compact ? SPACING_1 : SPACING_2)};
`;

const DuplicateContainer = styled.div<{ $compact: boolean }>`
  position: relative;
  margin-top: -${props => (props.$compact ? SPACING_1 : SPACING_2)};
  margin-left: -${props => (props.$compact ? SPACING_2 : SPACING_3)};
  margin-right: -${props => (props.$compact ? SPACING_2 : SPACING_3)};
`;

const SOCIAL_MEDIA_TYPES = new Set<MonitorMediaFilterType>([
  MonitorMediaFilterType.Facebook,
  MonitorMediaFilterType.Instagram,
  MonitorMediaFilterType.Twitter,
  MonitorMediaFilterType.Linkedin,
]);

const isSocialMediaType = (mediaTypeId?: MonitorMediaFilterType): boolean => {
  return mediaTypeId ? SOCIAL_MEDIA_TYPES.has(mediaTypeId) : false;
};

const getDisplayName = (
  mediaTypeId?: MonitorMediaFilterType,
  title?: string | null,
  sourceName?: string | null,
): string | null => {
  return isSocialMediaType(mediaTypeId) ? (title ?? null) : (sourceName ?? null);
};

const linkText = (mediaTypeId?: MonitorMediaFilterType): string => {
  switch (mediaTypeId) {
    case MonitorMediaFilterType.Web:
    case MonitorMediaFilterType.Print:
    case MonitorMediaFilterType.Blogs:
    case MonitorMediaFilterType.Forums:
      return 'monitor_mention-card_linkText-article';
    case MonitorMediaFilterType.TvPlay:
    case MonitorMediaFilterType.TvWeb:
    case MonitorMediaFilterType.Youtube:
    case MonitorMediaFilterType.Tiktok:
      return 'monitor_mention-card_linkText-video';
    case MonitorMediaFilterType.Podcast:
    case MonitorMediaFilterType.Radio:
      return 'monitor_mention-card_linkText-audio';
    case MonitorMediaFilterType.Twitter:
    case MonitorMediaFilterType.Instagram:
    case MonitorMediaFilterType.Linkedin:
    case MonitorMediaFilterType.Facebook:
      return 'monitor_mention-card_linkText-socialMedia';
    default:
      return 'monitor_mention-card_linkText-article';
  }
};

type MentionCardHit = {
  id: string;
  reach?: number;
  audioHitlines?: Array<{
    id: string;
    startPos: number;
    endPos: number;
    hitline: string;
  }>;
  mediaType2?: {
    id: MonitorMediaFilterType;
    name: string;
  };
  title?: string | null;
  sourceName?: string | null;
  thumbnailUrl?: string | null;
  publishedAt?: string;
  publishedBy?: {
    avatarUrl?: string | null;
    name?: string | null;
    link?: string | null;
  } | null;
  brandAiFilter?: {
    analysis?: string | null;
    brandSentiment?: Sentiment | null;
    brandSentimentDesc?: string | null;
    relevanceLevel?: number | null;
  } | null;
  audioUrl?: string | null;
  hitLine?: string | null;
  link?: string | null;
  duplicates?: MentionCardHit[];
  sourceVerification?: MonitorSourceVerification;
};

type MentionCardContentProps = {
  hit: MentionCardHit;
  evaluateIsSelected?: (id: string) => boolean;
  onToggleSelectedHit?: (id: string) => void;
  onSelectHits?: (ids: string[]) => void;
  onDeselectHits?: (ids: string[]) => void;
  showSelected?: boolean;
  compact?: boolean;
  onLinkClick?: MouseEventHandler<HTMLAnchorElement>;
  getDropdownItems?: (id: string, isGroup: boolean) => DropdownItem[];
  isDuplicate?: boolean;
  monitorSentiment?: boolean;
  checkSourceVerification?: boolean;
  duplicatesExpanded?: boolean;
  noPadding?: boolean;
};

const MentionCardContent = ({
  compact = false,
  onLinkClick,
  getDropdownItems,
  hit,
  isDuplicate = false,
  monitorSentiment,
  checkSourceVerification = false,
  duplicatesExpanded = false,
}: MentionCardContentProps) => {
  const { t } = useTranslation();
  const [showMultipleAudioHitlines, setShowMultipleAudioHitlines] = useState(false);

  const {
    material: { timezone, locale },
  } = useTimeSettings();

  const {
    sourceVerification,
    reach,
    duplicates,
    audioHitlines,
    mediaType2,
    title,
    sourceName,
    thumbnailUrl,
    publishedAt,
    publishedBy,
    brandAiFilter,
    audioUrl,
    hitLine,
    link,
  } = hit;

  const isGroup = Boolean(duplicates && duplicates.length > 0 && !duplicatesExpanded);

  const firstAudioHitLine = audioHitlines?.[0];
  const dropdownItems = getDropdownItems?.(hit.id, isGroup) ?? [];

  const { analysis, brandSentiment, brandSentimentDesc, relevanceLevel } = brandAiFilter || {};

  const shouldShowReach =
    checkSourceVerification && sourceVerification
      ? sourceVerification !== MonitorSourceVerification.Rejected
      : true;

  const localizedReach =
    reach != null && reach > 0 && localizeValue(reach, { locale: locale || 'en-US' });

  const hasDuplicates = duplicates && duplicates.length > 0;
  const hasMultipleAudioHitlines = audioHitlines && audioHitlines.length > 1;

  const isSocialMediaPost = isSocialMediaType(mediaType2?.id);
  const name = getDisplayName(mediaType2?.id, title, sourceName);

  const maxDuplicatesNamesToShow = 4;
  const duplicatesNamesToShow =
    duplicates
      ?.slice(0, maxDuplicatesNamesToShow)
      .map(duplicate =>
        getDisplayName(duplicate.mediaType2?.id, duplicate.title, duplicate.sourceName),
      )
      .filter((name): name is string => name !== null) ?? [];

  const names = [name, ...duplicatesNamesToShow].filter(Boolean).join(', ');
  const duplicatesCount = duplicates?.length ?? 0;
  const remainingCount = duplicatesCount - duplicatesNamesToShow.length;
  const plusText = remainingCount > 0 ? ` + ${remainingCount}` : '';

  return (
    <MentionCardContentContainer>
      <MentionCardHeader $compact={compact}>
        <MentionCardHeaderLeft $compact={compact}>
          <AvatarContainer $compact={compact}>
            {hasDuplicates &&
              !duplicatesExpanded &&
              duplicates
                .slice(0, 4)
                .reverse()
                .map(duplicateItem => (
                  <MentionCardAvatar
                    key={duplicateItem.id}
                    mediaFilterType={duplicateItem.mediaType2?.id}
                    thumbnailUrl={duplicateItem.thumbnailUrl}
                    compact={compact}
                  />
                ))}
            <MentionCardAvatar
              mediaFilterType={mediaType2?.id}
              thumbnailUrl={thumbnailUrl}
              compact={compact}
            />
          </AvatarContainer>
          <SourceContainer $compact={compact}>
            {names && (
              <SourceName $compact={compact}>
                {hasDuplicates && !isDuplicate && !duplicatesExpanded
                  ? t('monitor_mention_card_duplicate_hits', {
                      count: duplicates?.length + 1,
                    }) + `: ${names}${plusText}`
                  : name}
              </SourceName>
            )}
            {mediaType2?.id && (
              <MediaType id={mediaType2?.id} name={mediaType2?.name} compact={compact} />
            )}
          </SourceContainer>
        </MentionCardHeaderLeft>
        <MentionCardHeaderRight $compact={compact}>
          <MentionCardHeaderRightContent $compact={compact}>
            {publishedAt && (
              <LiveTime date={publishedAt} timezone={timezone} locale={locale} compact={compact} />
            )}
            {dropdownItems.length > 0 && <Dropdown items={dropdownItems} />}
          </MentionCardHeaderRightContent>
        </MentionCardHeaderRight>
      </MentionCardHeader>
      <div>
        {title && !isSocialMediaPost && <Title $compact={compact}>{title}</Title>}
        {audioUrl && !!audioHitlines?.length ? (
          <AudioPlayerContainer $compact={compact}>
            {audioHitlines.map((hitline, index) => (
              <AudioPlayer
                key={index}
                audioUrl={audioUrl}
                audioHitline={hitline}
                compact={compact}
                visible={showMultipleAudioHitlines ? true : index === 0}
              />
            ))}
          </AudioPlayerContainer>
        ) : (
          hitLine && <Hitline $compact={compact} dangerouslySetInnerHTML={{ __html: hitLine }} />
        )}
      </div>
      <MentionCardFooter $compact={compact}>
        <Metadata $compact={compact}>
          {hasMultipleAudioHitlines && (
            <ShowMultipleAudioHitlines
              type="button"
              onClick={() => setShowMultipleAudioHitlines(!showMultipleAudioHitlines)}
            >
              {showMultipleAudioHitlines ? (
                <MetadataItem>
                  <IconChevronUp $size={compact ? 'xxxs' : 'xxs'} />
                  {t('monitor_mention-card_audio-minimize')}
                </MetadataItem>
              ) : (
                <MetadataItem>
                  <IconChevronDown $size={compact ? 'xxxs' : 'xxs'} />
                  {t('monitor_mention-card_audio-mentions', {
                    count: audioHitlines?.length,
                  })}
                </MetadataItem>
              )}
            </ShowMultipleAudioHitlines>
          )}
          {link && (
            <MetadataExternalLink
              href={formatLinkWithSeekParam(link, firstAudioHitLine?.startPos)}
              onClick={onLinkClick}
              target="_blank"
              rel="noopener"
            >
              <MetadataItem>
                <IconExternalLink $size={compact ? 'xxxs' : 'xxs'} />
                {t(linkText(mediaType2?.id))}
              </MetadataItem>
            </MetadataExternalLink>
          )}
          {!monitorSentiment && shouldShowReach && localizedReach && (
            <MetadataItem>
              <IconReach $size={compact ? 'xxxs' : 'xxs'} />
              <span>{localizedReach}</span>
            </MetadataItem>
          )}
          {publishedBy &&
            (publishedBy.avatarUrl ? (
              publishedBy.link ? (
                <MetadataLink to={publishedBy.link}>
                  <MetadataItem>
                    <Avatar
                      type="contact"
                      size="xxs"
                      image={publishedBy.avatarUrl}
                      name={publishedBy.name || ''}
                    />
                    {publishedBy.name}
                  </MetadataItem>
                </MetadataLink>
              ) : (
                <MetadataItem>
                  <Avatar
                    type="contact"
                    size="xxs"
                    image={publishedBy.avatarUrl}
                    name={publishedBy.name || ''}
                  />
                  {publishedBy.name}
                </MetadataItem>
              )
            ) : publishedBy.link ? (
              <MetadataItem>
                {t('common_by')}{' '}
                <MetadataLink to={publishedBy.link}>{publishedBy.name}</MetadataLink>
              </MetadataItem>
            ) : (
              <MetadataItem>
                {t('common_by')} {publishedBy.name}
              </MetadataItem>
            ))}
        </Metadata>
        {!isDuplicate && (analysis || brandSentiment || relevanceLevel) && (
          <AISummary
            compact={compact}
            analysis={analysis}
            brandSentiment={brandSentiment}
            relevanceLevel={relevanceLevel}
            brandSentimentDesc={brandSentimentDesc}
          />
        )}
      </MentionCardFooter>
    </MentionCardContentContainer>
  );
};

const getPadding = (compact: boolean, hasDuplicates?: boolean) => {
  const spacing = compact ? SPACING_2 : SPACING_3;
  return hasDuplicates ? `${spacing} ${spacing} 0 ${spacing}` : spacing;
};

const MentionCardContainer = styled.div<{
  $compact: boolean;
  $hasDuplicates?: boolean;
  $noPadding?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${props => (props.$compact ? SPACING_2 : SPACING_3)};
  border-radius: ${RADIUS_LG};
  padding: ${({ $compact, $hasDuplicates, $noPadding }) =>
    $noPadding ? '0' : getPadding($compact, $hasDuplicates)};
  background-color: ${NEUTRAL_WHITE};
`;

const ToggleDuplicatesButton = styled.button<{ $compact: boolean }>`
  display: flex;
  height: 48px;
  gap: 8px;
  align-items: center;
  justify-content: center;
  line-height: 0;
  outline: none;
  background-color: transparent;
  border: 0;
  border-top: 2px solid ${GREY_200};
  padding: 0;
  margin-left: -${props => (props.$compact ? SPACING_2 : SPACING_3)};
  margin-right: -${props => (props.$compact ? SPACING_2 : SPACING_3)};
  font-size: ${props => (props.$compact ? SIZING_1_5 : '14px')};
  font-weight: 500;
  color: ${GREY_1000};
`;

const Divider = styled.div<{ $compact: boolean }>`
  border-top: 2px solid ${GREY_200};
  margin-left: -${props => (props.$compact ? SPACING_2 : SPACING_3)};
  margin-right: -${props => (props.$compact ? SPACING_2 : SPACING_3)};
`;

const MentionCard = (props: MentionCardContentProps) => {
  const { t } = useTranslation();
  const [duplicatesExpanded, setDuplicatesExpanded] = useState(false);
  const {
    hit,
    compact = false,
    noPadding = false,
    evaluateIsSelected,
    onToggleSelectedHit,
    onSelectHits,
    onDeselectHits,
    getDropdownItems,
  } = props;
  const duplicates = hit.duplicates;
  const hasDuplicates = duplicates && duplicates.length > 0;

  const allIds = [hit.id, ...(duplicates?.map(duplicate => duplicate.id) || [])].filter(toBoolean);
  const groupState = allIds.every(id => evaluateIsSelected?.(id) ?? false)
    ? 'checked'
    : allIds.some(id => evaluateIsSelected?.(id) ?? false)
      ? 'indeterminate'
      : 'unchecked';

  const selectAllIncludingDuplicates = () => {
    if (!onSelectHits || !evaluateIsSelected)
      throw new Error('onSelectHits or evaluateIsSelected is not defined');
    const unselectedIds = allIds.filter(id => !evaluateIsSelected(id));
    if (unselectedIds.length > 0) {
      onSelectHits(unselectedIds);
    }
  };
  const deselectAllIncludingDuplicates = () => {
    if (!onDeselectHits || !evaluateIsSelected)
      throw new Error('onDeselectHits or evaluateIsSelected is not defined');
    const selectedIds = allIds.filter(id => evaluateIsSelected(id));
    if (selectedIds.length > 0) {
      onDeselectHits(selectedIds);
    }
  };

  return (
    <MentionCardContainer $compact={compact} $hasDuplicates={hasDuplicates} $noPadding={noPadding}>
      {props.showSelected && onToggleSelectedHit && evaluateIsSelected && (
        <CheckboxContainer>
          <Checkbox
            state={
              !duplicatesExpanded
                ? groupState
                : (evaluateIsSelected?.(hit.id) ?? false)
                  ? 'checked'
                  : 'unchecked'
            }
            onChange={() => {
              if (
                !duplicatesExpanded &&
                (groupState === 'indeterminate' || groupState === 'unchecked')
              ) {
                selectAllIncludingDuplicates();
              } else if (!duplicatesExpanded && groupState === 'checked') {
                deselectAllIncludingDuplicates();
              } else {
                onToggleSelectedHit(hit.id);
              }
            }}
          />
        </CheckboxContainer>
      )}
      <MentionCardContent {...props} compact={compact} duplicatesExpanded={duplicatesExpanded} />
      {hasDuplicates &&
        duplicatesExpanded &&
        duplicates.map(duplicate => (
          <Fragment key={duplicate.id}>
            <Divider $compact={compact} />
            <DuplicateContainer $compact={compact}>
              {props.showSelected && onToggleSelectedHit && evaluateIsSelected && (
                <CheckboxContainer>
                  <Checkbox
                    onChange={() => onToggleSelectedHit(duplicate.id)}
                    checked={evaluateIsSelected(duplicate.id) ?? false}
                  />
                </CheckboxContainer>
              )}
            </DuplicateContainer>
            <MentionCardContent
              hit={duplicate}
              compact={compact}
              isDuplicate={true}
              getDropdownItems={getDropdownItems}
            />
          </Fragment>
        ))}
      {hasDuplicates && (
        <ToggleDuplicatesButton
          onClick={() => setDuplicatesExpanded(!duplicatesExpanded)}
          $compact={compact}
          aria-expanded={duplicatesExpanded}
          type="button"
        >
          {duplicatesExpanded ? (
            <>
              <IconChevronUp $size={compact ? 'xxxs' : 'xxs'} />
              {t('monitor_mention-card_hide-duplicates', {
                count: duplicates?.length + 1,
              })}
            </>
          ) : (
            <>
              <IconChevronDown $size={compact ? 'xxxs' : 'xxs'} />
              {t('monitor_mention-card_show-duplicates', {
                count: duplicates?.length + 1,
              })}
            </>
          )}
        </ToggleDuplicatesButton>
      )}
    </MentionCardContainer>
  );
};

export default MentionCard;
